import React from "react";
import { format, differenceInMinutes, max, min, isBefore } from "date-fns";

const TimeBar = ({
  reservations,
  selectedDay = new Date(),
  selected,
}) => {
  const today = format(selectedDay, "yyyy-MM-dd");
  // Create day boundaries as local time
  const dayStart = new Date(`${today}T00:00:00`);
  const dayEnd = new Date(`${today}T23:59:59`);
  const totalMinutes = 24 * 60;
  const minDistance = 8; // percent, labels closer than this will be hidden

  // 合併重疊時段
  function mergeContinuousReservations(reservations) {
    if (!reservations.length) return [];
    const sorted = [...reservations].sort(
      (a, b) => new Date(a.StartTime) - new Date(b.StartTime)
    );
    const merged = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
      const prev = merged[merged.length - 1];
      const current = sorted[i];
      const prevEnd = new Date(prev.EndTime);
      const currStart = new Date(current.StartTime);
      if (currStart <= prevEnd) {
        const currEnd = new Date(current.EndTime);
        prev.EndTime = new Date(Math.max(prevEnd, currEnd)).toISOString();
      } else {
        merged.push(current);
      }
    }
    return merged;
  }

  // 剪裁+合併
  const filtered = mergeContinuousReservations(
    reservations
      .map((res) => {
        const start = new Date(res.StartTime);
        const end = new Date(res.EndTime);
        const overlapStart = max([start, dayStart]);
        const overlapEnd = min([end, dayEnd]);
        if (isBefore(overlapEnd, overlapStart)) return null;
        return {
          StartTime: overlapStart.toISOString(),
          EndTime: overlapEnd.toISOString(),
        };
      })
      .filter(Boolean)
  );

  // 刻度 (每 2 小時一格)
  const ticks = Array.from({ length: 13 }, (_, i) => ({
    time: `${String(i * 2).padStart(2, "0")}:00`,
    percent: ((i * 120) / totalMinutes) * 100,
  }));

  // 收集所有 label 位置、內容
  const labels = [];
  filtered.forEach((r, i) => {
    const start = new Date(r.StartTime);
    const end = new Date(r.EndTime);
    const offsetMinutes = Math.max(0, differenceInMinutes(start, dayStart));
    const durationMinutes = Math.max(1, differenceInMinutes(end, start));
    const leftPercent = (offsetMinutes / totalMinutes) * 100;
    const widthPercent = (durationMinutes / totalMinutes) * 100;
    labels.push({
      idx: i,
      type: "start",
      percent: leftPercent,
      value: format(start, "HH:mm"),
    });
    labels.push({
      idx: i,
      type: "end",
      percent: leftPercent + widthPercent,
      value: format(end, "HH:mm"),
    });
  });

  // 根據 percent 排序，依序判斷是否太近
  labels.sort((a, b) => a.percent - b.percent);
  const shownLabels = [];
  let lastPercent = -Infinity;
  for (let i = 0; i < labels.length; i++) {
    if (labels[i].percent - lastPercent >= minDistance) {
      shownLabels.push({ ...labels[i], show: true });
      lastPercent = labels[i].percent;
    } else {
      shownLabels.push({ ...labels[i], show: false });
    }
  }

  // 幫助查找每個 reservation 的 start/end label 是否要顯示
  const showStartLabel = {};
  const showEndLabel = {};
  shownLabels.forEach((l) => {
    if (l.type === "start") showStartLabel[l.idx] = l.show;
    else showEndLabel[l.idx] = l.show;
  });

  // ==== Selected time highlight 處理 ====
  let selectedBar = null;
  let selectedLabel = null;

  if (selected && selected.StartTime && selected.EndTime) {
    const sStart = new Date(selected.StartTime);
    const sEnd = new Date(selected.EndTime);
    const overlapStart = max([sStart, dayStart]);
    const overlapEnd = min([sEnd, dayEnd]);
    if (!isBefore(overlapEnd, overlapStart)) {
      const start = overlapStart;
      const end = overlapEnd;
      const offsetMinutes = Math.max(
        0,
        differenceInMinutes(start, dayStart)
      );
      const durationMinutes = Math.max(1, differenceInMinutes(end, start));
      const leftPercent = (offsetMinutes / totalMinutes) * 100;
      const widthPercent = (durationMinutes / totalMinutes) * 100;
      selectedBar = (
        <div
          style={{
            position: "absolute",
            left: `${leftPercent}%`,
            width: `${widthPercent}%`,
            height: "100%",
            background:
              "linear-gradient(90deg, rgba(100,186,255,0.23), rgba(80,150,245,0.4) 60%, rgba(180,220,255,0.23) 100%)",
            borderRadius: 3,
            zIndex: 4,
            border: "1.5px solid #4390fa",
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        />
      );
      selectedLabel = (
        <div
          style={{
            position: "absolute",
            left: `${(leftPercent + leftPercent + widthPercent) / 2}%`,
            top: -20,
            transform: "translateX(-50%)",
            fontSize: 12,
            color: "#2579d2",
            fontWeight: 700,
            background: "#fff",
            borderRadius: 2,
            padding: "0 2px",
            zIndex: 6,
            pointerEvents: "none",
            border: "1px solid #d8eaff",
            boxShadow: "0 1px 2px #c7eaff55",
          }}
        >
          {`${format(start, "HH:mm")}~${format(end, "HH:mm")}`}
        </div>
      );
    }
  }

  return (
    <div style={{ width: "90%", maxWidth: 600, margin: "25px auto" }}>
      <div
        style={{
          position: "relative",
          height: 35,
          background: "#d1d1d1ff",
          borderRadius: 2,
          border: "1px solid #dde1ef",
          overflow: "visible",
        }}
      >
        {/* Selected Range Highlight */}
        {selectedBar}

        {/* Reservation Bars (藍色顯示) */}
        {filtered.map((r, i) => {
          const start = new Date(r.StartTime);
          const end = new Date(r.EndTime);
          const offsetMinutes = Math.max(
            0,
            differenceInMinutes(start, dayStart)
          );
          const durationMinutes = Math.max(1, differenceInMinutes(end, start));
          const leftPercent = (offsetMinutes / totalMinutes) * 100;
          const widthPercent = (durationMinutes / totalMinutes) * 100;
          return (
            <React.Fragment key={i}>
              {/* bar */}
              <div
                style={{
                  position: "absolute",
                  left: `${leftPercent}%`,
                  width: `${widthPercent}%`,
                  top: -1,
                  height: "100%",
                  background:
                    "linear-gradient(90deg, rgba(130,180,255,0.8), rgba(90,140,255,0.8))",
                  border: "1px solid #3a7ded",
                  borderRadius: 1,
                  boxShadow: "0 1px 4px rgba(50,100,200,0.35)",
                  zIndex: 2,
                }}
              // title={`${format(start, "HH:mm")}–${format(end, "HH:mm")}`}
              />

              {/* start tick */}
              {showStartLabel[i] && (
                <div
                  style={{
                    position: "absolute",
                    left: `${leftPercent}%`,
                    top: 37,
                    width: 0,
                    height: 7,
                    borderLeft: "1px solid #727272",
                    zIndex: 3,
                  }}
                />
              )}

              {/* end tick */}
              {showEndLabel[i] && (
                <div
                  style={{
                    position: "absolute",
                    left: `${leftPercent + widthPercent}%`,
                    top: 37,
                    marginLeft: -1.5,
                    width: 0,
                    height: 7,
                    borderLeft: "1px solid #727272",
                    zIndex: 3,
                  }}
                />
              )}

              {/* start label (hidden if too close) */}
              {showStartLabel[i] && (
                <div
                  style={{
                    position: "absolute",
                    left: `${leftPercent}%`,
                    top: 43,
                    transform: "translateX(-50%)",
                    fontSize: 12,
                    color: "#727272",
                    fontWeight: 600,
                    background: "transparent",
                    borderRadius: 1,
                    padding: "0 1px",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  {/* {format(start, "HH:mm")} */}
                </div>
              )}

              {/* end label (hidden if too close) */}
              {showEndLabel[i] && (
                <div
                  style={{
                    position: "absolute",
                    left: `${leftPercent + widthPercent}%`,
                    top: 43,
                    transform: "translateX(-50%)",
                    fontSize: 12,
                    color: "#727272",
                    fontWeight: 600,
                    background: "transparent",
                    borderRadius: 1,
                    padding: "0 1px",
                    zIndex: 3,
                    pointerEvents: "none",
                  }}
                >
                  {/* {format(end, "HH:mm")} */}
                </div>
              )}
            </React.Fragment>
          );
        })}

        {/* Selected time label (on top) */}
        {selectedLabel}

        {/* 刻度線 + 小時文字（只顯示前兩個字，如 "23"） */}

        {/* Tick lines + hour text (white, above bar) */}
        {ticks.map((t, i) => {
          const left = (i * 100) / 12;

          return (
            <React.Fragment key={t.time}>
              {/* hour text */}
              <div
                style={{
                  position: "absolute",
                  left: `${left}%`,
                  top: -18,                         // above the bar
                  transform: "translateX(-50%)",
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#ffffff",                 // white text
                  textShadow: "0px 1px 3px rgba(0,0,0,0.6)", // clearer against backgrounds
                  pointerEvents: "none",
                }}
              >
                {t.time.slice(0, 2)}
              </div>

              {/* vertical tick line */}
              <div
                style={{
                  position: "absolute",
                  left: `${left}%`,
                  top: 2,
                  height: 31,
                  width: 1,
                  background: "rgba(0, 0, 0, 0.5)", // faint white tick for contrast
                  zIndex: 1,
                }}
              />
            </React.Fragment>
          );
        })}


      </div>
    </div>
  );
};

export default TimeBar;
